"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState, useEffect } from "react";
import { cva } from "class-variance-authority";
import { cn, isSlotChain } from "../../../utils";
import { CheckIcon, SpinnerIcon, AlertIcon, AwardIcon, SparklesIcon, SparklesDraftIcon, TransactionIcon, SlotIcon, GlobeIcon, AddUserIcon, } from "../../../components/icons";
import { StarknetColorIcon, UsdColorIcon, } from "../../../components/icons/brand-color";
import { CollectibleImage } from "../../../components/modules/collectibles";
import { AchievementPlayerAvatar } from "../../../components/modules/achievements/player-avatar";
import { usePresetColor } from "../controller-toaster/preset-provider";
import { Toast } from "./toast";
// Base toast container for specialized toasts
const specializedToastVariants = cva("flex flex-col items-start p-0 bg-spacer-100 shadow-lg rounded-lg border-0 overflow-hidden relative", {
    variants: {
        variant: {
            achievement: "w-[360px] h-[68px]",
            user: "w-[360px] h-[68px]",
            setting: "w-[360px] h-[68px]",
            credits: "w-[360px] h-[68px]",
            network: "w-[360px] h-[52px]",
            error: "w-[360px] h-[52px] bg-destructive",
            transaction: "w-[360px] h-[52px]",
        },
    },
    defaultVariants: {
        variant: "achievement",
    },
});
const XPTag = memo(({ amount, isMainnet = true }) => (_jsxs("div", { className: "flex items-center gap-[2px]", children: [_jsx("div", { className: "w-5 h-5 flex items-center justify-center", children: isMainnet ? (_jsx(SparklesIcon, { variant: "solid", size: "sm", className: "text-foreground" })) : (_jsx(SparklesDraftIcon, { size: "sm", className: "text-foreground" })) }), _jsx("span", { className: "text-foreground text-sm font-normal leading-5", children: amount })] })));
XPTag.displayName = "XPTag";
const ToastProgressBar = memo(({ progress, variant = "achievement", className, preset, color }) => {
    const [animatedProgress, setAnimatedProgress] = useState(0);
    const presetColor = usePresetColor(preset);
    const barColor = color || presetColor;
    useEffect(() => {
        const timer = setTimeout(() => setAnimatedProgress(progress), 100);
        return () => clearTimeout(timer);
    }, [progress]);
    const getColors = () => {
        if (variant === "error") {
            return {
                bg: "bg-translucent-dark-100",
                fill: "bg-translucent-dark-200",
            };
        }
        return {
            bg: "bg-background-200",
            fill: barColor ? undefined : "bg-achievement",
        };
    };
    const colors = getColors();
    return (_jsx("div", { className: cn("absolute bottom-0 left-0 right-0 w-full h-1", colors.bg, className), children: _jsx("div", { className: cn("h-full transition-all duration-1000 ease-out", colors.fill), style: {
                width: `${animatedProgress}%`,
                backgroundColor: barColor ?? undefined,
            } }) }));
});
ToastProgressBar.displayName = "ToastProgressBar";
// Achievement Toast Component
const AchievementToast = memo(({ title, subtitle = "Earned!", xpAmount, progress = 100, isDraft = false, preset, duration, showClose, toastId, className, ...props }) => {
    const IconComponent = AwardIcon;
    const iconColor = isDraft
        ? "var(--achievement-200)"
        : "var(--achievement-100)";
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "achievement" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: [_jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center justify-center w-10 h-10 bg-background rounded p-[5px] flex-shrink-0", children: _jsx(IconComponent, { size: "lg", className: "min-w-6", style: { color: iconColor } }) }), _jsxs("div", { className: "flex flex-col justify-center gap-[2px] flex-1 min-w-0", children: [_jsx("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: title }), _jsx("span", { className: "text-foreground-300 text-xs font-normal leading-4 truncate", children: subtitle })] })] }), _jsx("div", { className: "flex items-center gap-2 flex-shrink-0 ml-2", children: _jsx(XPTag, { amount: xpAmount, isMainnet: !isDraft }) })] }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: isDraft ? null : preset, color: isDraft ? "var(--achievement-200)" : undefined })] }));
});
AchievementToast.displayName = "AchievementToast";
// Marketplace Toast Component
const MarketplaceToast = memo(({ action, collectionName, itemNames, itemImages, progress = 100, preset, showClose, toastId, className, ...props }) => {
    const title = `${action.charAt(0).toUpperCase()}${action.slice(1)}`;
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "achievement" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center justify-center w-10 h-10 bg-background rounded p-[5px] flex-shrink-0", children: _jsx(CollectibleImage, { images: itemImages }) }), _jsxs("div", { className: "flex flex-col justify-center gap-[2px] flex-1 min-w-0", children: [_jsx("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: title }), _jsx("span", { className: "text-foreground-300 text-xs font-normal leading-4 truncate", children: itemNames.length > 1
                                        ? `${itemNames.length} ${collectionName}`
                                        : (itemNames[0] ?? "1 Item") })] })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: preset })] }));
});
MarketplaceToast.displayName = "MarketplaceToast";
// Network Toast Component
const NETWORK_TOAST_ACTION = {
    connect: "Connected to",
    "switch-chain": "Switched to",
};
export const TOAST_SN_MAIN = "0x534e5f4d41494e";
export const TOAST_SN_SEPOLIA = "0x534e5f5345504f4c4941";
const getNameFromChainId = (chainId) => {
    // convert chain id hex to string without dependencies
    const hex = chainId.startsWith("0x") ? chainId.slice(2) : chainId;
    let name = "";
    for (let i = 0; i < hex.length; i += 2) {
        name += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
    }
    // replace "_" for spaces and capitalize each word
    return name
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
};
const NetworkToast = memo(({ kind, chainId, networkName, networkIcon, showClose, toastId, className, ...props }) => {
    const isMainnet = BigInt(chainId) === BigInt(TOAST_SN_MAIN);
    const isSepolia = BigInt(chainId) === BigInt(TOAST_SN_SEPOLIA);
    const icon = isMainnet || isSepolia ? (_jsx(StarknetColorIcon, { size: "default", className: "min-w-6 scale-125" })) : networkIcon ? (_jsx("img", { src: networkIcon, alt: "" })) : isSlotChain(chainId) ? (_jsx(SlotIcon, { size: "default", className: "min-w-6 scale-125" })) : (_jsx(GlobeIcon, { variant: "solid", size: "default", className: "min-w-6 scale-125" }));
    const name = isMainnet
        ? "Starknet Mainnet"
        : isSepolia
            ? "Starknet Sepolia"
            : networkName || getNameFromChainId(chainId);
    return (_jsx(Toast, { className: cn(specializedToastVariants({ variant: "network" }), className), showClose: showClose, toastId: toastId, ...props, children: _jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full h-full", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "w-6 h-6 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0", children: icon }), _jsxs("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: [NETWORK_TOAST_ACTION[kind], " ", name] })] }) }) }));
});
NetworkToast.displayName = "NetworkToast";
// Error Toast Component
const ErrorToast = memo(({ message, progress = 100, showClose, toastId, className, ...props }) => (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "error" }), className), showClose: showClose, toastId: toastId, ...props, variant: "destructive", children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx(AlertIcon, { size: "default", className: "text-destructive-foreground min-w-6" }), _jsx("span", { className: "text-destructive-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: message })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "error" })] })));
ErrorToast.displayName = "ErrorToast";
// Success Toast Component
const SuccessToast = memo(({ message, progress = 100, preset, showClose, toastId, className, ...props }) => (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "transaction" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx(CheckIcon, { size: "default", className: "text-foreground min-w-6" }), _jsx("span", { className: "text-foreground text-sm font-normal leading-5 tracking-[0.01em] truncate", children: message })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: preset })] })));
SuccessToast.displayName = "SuccessToast";
// User Toast Component
const USER_TOAST_DEFAULT_MESSAGE = {
    created: "Controller Created!",
    connected: "Controller Connected",
    disconnected: "Controller Disconnected",
};
const UserToast = memo(({ username, kind = "created", message, progress = 100, preset, showClose, toastId, className, ...props }) => {
    const title = message ?? USER_TOAST_DEFAULT_MESSAGE[kind];
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "user" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center justify-center w-10 h-10 bg-background rounded p-[5px] flex-shrink-0", children: _jsx(AchievementPlayerAvatar, { username: username, size: "lg", className: "text-foreground min-w-6" }) }), _jsxs("div", { className: "flex flex-col justify-center gap-[2px] flex-1 min-w-0", children: [_jsx("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: title }), _jsx("span", { className: "text-foreground-300 text-xs font-normal leading-4 truncate", children: username })] })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: preset })] }));
});
UserToast.displayName = "UserToast";
// Setting Toast Component
const SETTING_TOAST_ICON = {
    signer: _jsx(AddUserIcon, { size: "lg", className: "text-foreground min-w-6" }),
};
const SETTING_TOAST_MESSAGE = {
    signer: {
        created: "New signer created",
        deleted: "Signer deleted",
    },
};
const SettingToast = memo(({ kind, action, progress = 100, preset, showClose, toastId, className, ...props }) => {
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "setting" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center justify-center w-10 h-10 bg-background rounded p-[5px] flex-shrink-0", children: SETTING_TOAST_ICON[kind] }), _jsxs("div", { className: "flex flex-col justify-center gap-[2px] flex-1 min-w-0", children: [_jsx("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: "Success!" }), _jsx("span", { className: "text-foreground-300 text-xs font-normal leading-4 truncate", children: SETTING_TOAST_MESSAGE[kind][action] })] })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: preset })] }));
});
SettingToast.displayName = "SettingToast";
// Credits Toast Component
const CREDITS_TOAST_TITLE = {
    deposit: {
        initiated: "Deposit Initiated",
        completed: "Deposit Complete",
    },
    withdraw: {
        initiated: "Withdraw Initiated",
        completed: "Withdraw Complete",
    },
};
const CreditsToast = memo(({ kind, status, amount, progress = 100, preset, showClose, toastId, className, ...props }) => {
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "credits" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center justify-center w-10 h-10 bg-background rounded p-[5px] flex-shrink-0", children: _jsx(UsdColorIcon, { size: "lg", className: "min-w-6" }) }), _jsxs("div", { className: "flex flex-col justify-center gap-[2px] flex-1 min-w-0", children: [_jsx("span", { className: "text-foreground text-sm font-medium leading-5 tracking-[0.01em] truncate", children: CREDITS_TOAST_TITLE[kind][status] }), _jsxs("span", { className: "text-foreground-300 text-xs font-normal leading-4 truncate", children: [amount, " Credits ($", amount.toFixed(2), ")"] })] })] }) }), _jsx(ToastProgressBar, { progress: progress, variant: "achievement", preset: preset })] }));
});
CreditsToast.displayName = "CreditsToast";
// Transaction Notification Component
const TransactionToast = memo(({ status, isExpanded = true, label, progress = 100, preset, showClose, toastId, className, ...props }) => {
    if (!isExpanded) {
        return (_jsx(Toast, { className: "flex items-center justify-center p-[10px] w-12 h-12 bg-background shadow-lg rounded-lg border-0 overflow-hidden", showClose: !isExpanded ? false : showClose, toastId: toastId, ...props, children: _jsx("div", { className: "w-7 h-7 flex items-center justify-center", children: status === "confirming" ? (_jsx(SpinnerIcon, { size: "default", className: "text-achievement animate-spin min-w-6" })) : (_jsx(CheckIcon, { size: "default", className: "text-achievement min-w-6" })) }) }));
    }
    return (_jsxs(Toast, { className: cn(specializedToastVariants({ variant: "transaction" }), className), showClose: showClose, toastId: toastId, ...props, children: [_jsx("div", { className: "flex items-center justify-between px-3 py-3 w-full flex-1", children: _jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "w-6 h-6 flex items-center justify-center", children: status === "confirming" ? (_jsx(SpinnerIcon, { size: "default", className: "text-foreground animate-spin min-w-6" })) : (_jsx(CheckIcon, { size: "default", className: "text-foreground min-w-6" })) }), _jsx("span", { className: "text-foreground text-sm font-normal leading-5 tracking-[0.01em] truncate", children: status === "confirming" ? "Confirming" : "Confirmed" }), status === "confirming" && (_jsxs("div", { className: "flex items-center py-1 bg-translucent-dark-100 rounded-[2px] flex-shrink-0", children: [_jsx("div", { className: "w-4 h-4 mr-1 flex items-center justify-center", children: _jsx(TransactionIcon, { className: "w-[11px] h-[9px] text-achievement" }) }), _jsx("span", { className: "text-achievement text-xs font-normal leading-4 whitespace-nowrap", children: label })] }))] }) }), _jsx(ToastProgressBar, { progress: status === "confirmed" ? 100 : progress, variant: "achievement", preset: preset })] }));
});
TransactionToast.displayName = "TransactionToast";
export const showAchievementToast = (props) => {
    const toastId = props.toastId || `achievement-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(AchievementToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showMarketplaceToast = (props) => {
    const toastId = props.toastId || `marketplace-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(MarketplaceToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showNetworkToast = (props) => {
    const toastId = props.toastId || `network-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(NetworkToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showErrorToast = (props) => {
    const toastId = props.toastId || `error-${Date.now()}`;
    return {
        variant: "destructive",
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(ErrorToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showSuccessToast = (props) => {
    const toastId = props.toastId || `success-${Date.now()}`;
    return {
        variant: "default",
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(SuccessToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showUserToast = (props) => {
    const toastId = props.toastId || `user-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(UserToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showSettingToast = (props) => {
    const toastId = props.toastId || `setting-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(SettingToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showCreditsToast = (props) => {
    const toastId = props.toastId || `credits-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(CreditsToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export const showTransactionToast = (props) => {
    const toastId = props.toastId || `transaction-${Date.now()}`;
    return {
        duration: props.duration,
        toasterId: props.toasterId,
        toastId,
        element: _jsx(TransactionToast, { ...props, showClose: true, toastId: toastId }),
    };
};
export { AchievementToast, MarketplaceToast, NetworkToast, ErrorToast, SuccessToast, UserToast, SettingToast, CreditsToast, TransactionToast, XPTag, ToastProgressBar, };
//# sourceMappingURL=specialized-toasts.js.map